import { ActionTree, GetterTree, MutationTree } from "vuex";
import { logError } from "@/lib/utils/logger";

// コンテンツタイプの定義
export type AssistContentType =
  | "request" // 依頼選択画面（初期状態）
  | "manuscript-select" // 原稿選択画面
  | "result" // 結果表示画面
  | "history"; // 履歴画面

// 情報バナー状態の型定義
export interface InfoBannerState {
  isVisible: boolean;
  isDismissed: boolean;
}

// State型定義
export interface AssistNavigationState {
  currentContent: AssistContentType;
  currentTab: string;
  error: string | null;
  isVisible: boolean;
  selectedItemId: string | null; // 選択中のアイテムID
  infoBanner: InfoBannerState; // 情報バナー状態
}

// LocalStorage管理のヘルパー関数
const INFO_BANNER_KEY_PREFIX = "nola:assist-info-banner:";

function getInfoBannerKey(userId: string): string {
  return `${INFO_BANNER_KEY_PREFIX}${userId}`;
}

function saveInfoBannerState(userId: string, isDismissed: boolean): void {
  try {
    const key = getInfoBannerKey(userId);
    localStorage.setItem(key, JSON.stringify({ isDismissed }));
  } catch (error) {
    logError({ module: "assistNavigationModule", action: "saveInfoBannerState" }, "情報バナー状態の保存に失敗", error);
  }
}

function loadInfoBannerState(userId: string): { isDismissed: boolean } {
  try {
    const key = getInfoBannerKey(userId);
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { isDismissed: parsed.isDismissed || false };
    }
  } catch (error) {
    logError(
      { module: "assistNavigationModule", action: "loadInfoBannerState" },
      "情報バナー状態の読み込みに失敗",
      error
    );
  }
  return { isDismissed: false };
}

// Mutations型定義
interface Mutations {
  setCurrentContent(state: AssistNavigationState, content: AssistContentType): void;
  setCurrentTab(state: AssistNavigationState, tab: string): void;
  setError(state: AssistNavigationState, error: string | null): void;
  setVisible(state: AssistNavigationState, visible: boolean): void;
  setSelectedItemId(state: AssistNavigationState, id: string | null): void;
  setInfoBannerVisible(state: AssistNavigationState, visible: boolean): void;
  setInfoBannerDismissed(state: AssistNavigationState, dismissed: boolean): void;
  resetState(state: AssistNavigationState): void;
}

// Actions型定義
interface Actions {
  showManuscriptSelect(context: any): Promise<void>;
  showRequestContent(context: any): Promise<void>;
  showResult(context: any): Promise<void>;
  showResultWithId(context: any, payload: { id: string }): Promise<void>;
  showHistory(context: any): Promise<void>;
  showPanel(context: any): Promise<void>;
  hidePanel(context: any): Promise<void>;
  setCurrentTab(context: any, tab: string): Promise<void>;
  setError(context: any, error: string | null): Promise<void>;
  clearError(context: any): Promise<void>;
  initializeInfoBannerState(context: any): Promise<void>;
  dismissInfoBanner(context: any): Promise<void>;
  resetInfoBanner(context: any): Promise<void>;
  resetState(context: any): Promise<void>;
}

// Getters型定義
interface Getters {
  currentContent(state: AssistNavigationState): AssistContentType;
  currentTab(state: AssistNavigationState): string;
  error(state: AssistNavigationState): string | null;
  isVisible(state: AssistNavigationState): boolean;
  selectedItemId(state: AssistNavigationState): string | null;
  infoBannerVisible(state: AssistNavigationState): boolean;
  shouldShowInfoBanner(state: AssistNavigationState): boolean;
}

// Mutations
const mutations: MutationTree<AssistNavigationState> & Mutations = {
  setCurrentContent(state, content) {
    state.currentContent = content;
  },
  setCurrentTab(state, tab) {
    state.currentTab = tab;
  },
  setError(state, error) {
    state.error = error;
  },
  setVisible(state, visible) {
    state.isVisible = visible;
  },
  setSelectedItemId(state, id) {
    state.selectedItemId = id;
  },
  setInfoBannerVisible(state, visible) {
    state.infoBanner.isVisible = visible;
  },
  setInfoBannerDismissed(state, dismissed) {
    state.infoBanner.isDismissed = dismissed;
  },
  resetState(state) {
    state.currentContent = "request";
    state.currentTab = "assist";
    state.error = null;
    state.isVisible = false;
    state.selectedItemId = null;
    // 情報バナー状態はリセットしない（ユーザーの解除状態を保持）
  },
};

// Actions
const actions: ActionTree<AssistNavigationState, {}> & Actions = {
  async showManuscriptSelect({ commit }) {
    try {
      commit("setError", null);

      // 画面遷移の処理
      await new Promise((resolve) => setTimeout(resolve, 100)); // 最小ローディング時間

      commit("setCurrentContent", "manuscript-select");
      commit("setCurrentTab", "assist");
    } catch (error) {
      logError({ module: "assistNavigationModule", action: "showManuscriptSelect" }, "原稿選択画面の表示に失敗", error);
      commit("setError", "原稿選択画面の表示に失敗しました");
    }
  },

  async showRequestContent({ commit }) {
    try {
      commit("setError", null);

      await new Promise((resolve) => setTimeout(resolve, 100));

      commit("setCurrentContent", "request");
      commit("setCurrentTab", "assist");
    } catch (error) {
      logError({ module: "assistNavigationModule", action: "showRequestContent" }, "依頼選択画面の表示に失敗", error);
      commit("setError", "依頼選択画面の表示に失敗しました");
    }
  },

  async showResult({ commit }) {
    try {
      commit("setError", null);

      await new Promise((resolve) => setTimeout(resolve, 100));

      commit("setCurrentContent", "result");
      commit("setCurrentTab", "assist");
    } catch (error) {
      logError({ module: "assistNavigationModule", action: "showResult" }, "結果画面の表示に失敗", error);
      commit("setError", "結果画面の表示に失敗しました");
    }
  },

  async showResultWithId({ commit }, payload) {
    try {
      commit("setError", null);

      // 選択されたアイテムIDを保存
      commit("setSelectedItemId", payload.id);

      await new Promise((resolve) => setTimeout(resolve, 100));

      commit("setCurrentContent", "result");
      commit("setCurrentTab", "assist");
    } catch (error) {
      logError({ module: "assistNavigationModule", action: "showResultWithId" }, "結果画面の表示に失敗", error);
      commit("setError", "結果画面の表示に失敗しました");
    }
  },

  async showHistory({ commit }) {
    try {
      commit("setError", null);

      await new Promise((resolve) => setTimeout(resolve, 100));

      commit("setCurrentContent", "history");
      commit("setCurrentTab", "history");
    } catch (error) {
      logError({ module: "assistNavigationModule", action: "showHistory" }, "履歴画面の表示に失敗", error);
      commit("setError", "履歴画面の表示に失敗しました");
    }
  },

  async showPanel({ commit, dispatch }) {
    try {
      commit("setVisible", true);
      // パネル表示時に情報バナー状態を初期化
      await dispatch("initializeInfoBannerState");
    } catch (error) {
      logError({ module: "assistNavigationModule", action: "showPanel" }, "パネルの表示に失敗", error);
      commit("setError", "パネルの表示に失敗しました");
    }
  },

  async hidePanel({ commit }) {
    try {
      // パネルを閉じる前に状態をリセット
      commit("setError", null);
      commit("setCurrentContent", "request");
      commit("setCurrentTab", "assist"); // currentTabもリセット
      commit("setSelectedItemId", null);
      commit("setInfoBannerVisible", false);
      commit("setVisible", false);
    } catch (error) {
      logError({ module: "assistNavigationModule", action: "hidePanel" }, "パネルの非表示に失敗", error);
      commit("setError", "パネルの非表示に失敗しました");
    }
  },

  async setCurrentTab({ commit }, tab) {
    commit("setCurrentTab", tab);
  },

  async setError({ commit }, error) {
    commit("setError", error);
  },

  async clearError({ commit }) {
    commit("setError", null);
  },

  async initializeInfoBannerState({ commit, rootGetters }) {
    try {
      const user = rootGetters["userModule/user"];
      if (!user || !user.userId) {
        commit("setInfoBannerVisible", false);
        return;
      }

      // LocalStorageから状態を読み込み
      const { isDismissed } = loadInfoBannerState(user.userId);
      commit("setInfoBannerDismissed", isDismissed);

      // 解除されていない場合は表示
      commit("setInfoBannerVisible", !isDismissed);
    } catch (error) {
      logError(
        { module: "assistNavigationModule", action: "initializeInfoBannerState" },
        "情報バナー状態の初期化に失敗",
        error
      );
      commit("setInfoBannerVisible", false);
    }
  },

  async dismissInfoBanner({ commit, rootGetters }) {
    try {
      const user = rootGetters["userModule/user"];
      if (!user || !user.userId) {
        logError(
          { module: "assistNavigationModule", action: "dismissInfoBanner" },
          "ユーザー情報が取得できません",
          null
        );
        return;
      }

      // LocalStorageに解除状態を保存
      saveInfoBannerState(user.userId, true);

      // 状態を更新
      commit("setInfoBannerDismissed", true);
      commit("setInfoBannerVisible", false);
    } catch (error) {
      logError({ module: "assistNavigationModule", action: "dismissInfoBanner" }, "情報バナーの解除に失敗", error);
    }
  },

  async resetInfoBanner({ commit, rootGetters }) {
    try {
      const user = rootGetters["userModule/user"];
      if (!user || !user.userId) {
        return;
      }

      // LocalStorageから解除状態を削除
      const key = getInfoBannerKey(user.userId);
      localStorage.removeItem(key);

      // 状態をリセット
      commit("setInfoBannerDismissed", false);
      commit("setInfoBannerVisible", true);
    } catch (error) {
      logError({ module: "assistNavigationModule", action: "resetInfoBanner" }, "情報バナーのリセットに失敗", error);
    }
  },

  async resetState({ commit }) {
    commit("resetState");
  },
};

// Getters
const getters: GetterTree<AssistNavigationState, {}> & Getters = {
  currentContent: (state) => state.currentContent,
  currentTab: (state) => state.currentTab,
  error: (state) => state.error,
  isVisible: (state) => state.isVisible,
  selectedItemId: (state) => state.selectedItemId,
  infoBannerVisible: (state) => state.infoBanner.isVisible,
  shouldShowInfoBanner: (state) =>
    // アシストパネル表示中 AND リクエストコンテンツ AND 解除されていない場合に表示
    state.isVisible &&
    state.currentContent === "request" &&
    !state.infoBanner.isDismissed &&
    state.infoBanner.isVisible,
};

// 初期状態
const state: AssistNavigationState = {
  currentContent: "request",
  currentTab: "assist",
  error: null,
  isVisible: false,
  selectedItemId: null,
  infoBanner: {
    isVisible: false,
    isDismissed: false,
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
